import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { z } from 'zod/v4';
import { useIxcRegistrationV1ProfileQueryServiceProfileGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-query-service-profile-get';
import { useIxcRegistrationV1ProfileVersionQueryServiceProfileVersionGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-profile-version-query-service-profile-version-get';
import { TenantContext, useActiveRealmAPIContext } from '@interxfi/api-client/realm-context';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE, PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess, useIsWriteAllowed } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { ButtonLink } from '@interxfi/design-system/components/button/button-link';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { Hint } from '@interxfi/design-system/components/hint/hint';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { TextButton } from '@interxfi/design-system/components/link/text-link/text-button';
import { baseTextLinkTypographyBySize, TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Pill } from '@interxfi/design-system/components/pill/pill';
import type { TypographyProps } from '@interxfi/design-system/components/typography';
import { Typography } from '@interxfi/design-system/components/typography';
import type { Space } from '@interxfi/design-system/styles/tokens';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import { ProfileAccountTiles } from '../../../../../../../features/registration/profile/account-tiles/profile-account-tiles';
import { ProfileCheckWorkflowDrawer } from '../../../../../../../features/registration/profile/checks/profile-check-workflow-drawer/profile-check-workflow-drawer';
import { WatchlistAndChecksSummary } from '../../../../../../../features/registration/profile/checks/watchlist-and-checks-summary/watchlist-and-checks-summary';
import { OrganizationProfileControlPersonsTable } from '../../../../../../../features/registration/profile/organization/organization-profile-control-persons-table';
import { RegistrationProfileEntityMenu } from '../../../../../../../features/registration/profile/profile-entity-menu';
import { ProfileStatusPill } from '../../../../../../../features/registration/profile/profile-status-pill';
import {
  checkIfProfileCanBePrimaryAccountHolder,
  getProfileName,
  getW9SignedDateForProfile,
} from '../../../../../../../features/registration/profile/profile-utils';
import { getRegistrationProfileDetailPath } from '../../../../../../../features/registration/profile/registration-profile-routing';
import { getRegistrationProfileVersionListItemSummary } from '../../../../../../../features/registration/profile/version/profile-version-list-items';
import { getCanActionProfileCheck } from '../../../../../../../features/registration/shared-utils/registration-permission-helpers';
import { ProgramLogomark } from '../../../../../../../features/userauth/program/logomark/program-logomark';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

const searchSchema = z.object({
  versionId: z.string().optional(),
});

function ProfileDetailBreadcrumb({ profileId }: { profileId: string }) {
  const { data, isPending } = useIxcRegistrationV1ProfileQueryServiceProfileGet({ profileId });

  if (isPending) {
    return <Skeleton charCount={12} />;
  }

  return getProfileName(data?.profile) || `Profile ${getShortenedId(profileId)}`;
}

export const Route = createFileRoute('/_app/_clearing-core/registration/profile/_profile/$profileId/')({
  validateSearch: searchSchema,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `profile-${params.profileId}`,
        label: <ProfileDetailBreadcrumb profileId={params.profileId} />,
        to: getRegistrationProfileDetailPath(params.profileId),
        suffix: <CopyButton content={params.profileId} label="Copy Profile ID" tooltip={{ copy: params.profileId, copied: 'Copied Profile ID' }} />,
      }),
    }) as const satisfies AggregateBreadcrumb,
  head: () => getPageHead('Profile'),
  component: ProfileDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load profile', 'view this profile');

const statusBannerCtaTypography = {
  ...baseTextLinkTypographyBySize.small,
  tone: 'background',
  inheritance: 'current',
} as const satisfies TypographyProps;

// The 1px offset is to allow the header to fit in its container without overflowing, because the program logomark uses a box shadow instead of a border
// when an image is provided. If the overflow property on the container is changed instead, the organization profile's control person table overflows.
const pxOffsetForHeader = {
  margin: '1px 0 0 1px',
} as const;

const gap: ResponsiveValue<Space> = {
  sm: 7,
  md: 10,
};

const right = {
  width: { type: 'fixed', width: 320 },
} as const;

function ProfileDetail() {
  const { profileId } = Route.useParams();
  const { versionId } = Route.useSearch();
  const isWriteAllowed = useIsWriteAllowed();
  const { data, isPending, error } = useIxcRegistrationV1ProfileQueryServiceProfileGet({ profileId });
  const selectedVersionId =
    versionId ?? data?.profile?.data?.pendingVersion?.profileVersionId ?? data?.profile?.data?.currentVersion?.profileVersionId;
  const {
    data: versionData,
    isPending: versionIsPending,
    error: versionError,
  } = useIxcRegistrationV1ProfileVersionQueryServiceProfileVersionGet(selectedVersionId ? { profileVersionId: selectedVersionId } : undefined);
  useErrorHandler(error || versionError, errorMessageGetter);
  const { activeTenantContext } = useActiveRealmAPIContext();
  const isClearingUser = Boolean(activeTenantContext === TenantContext.Clearing);
  const actionableChecks = versionData?.profileVersion?.data?.checks?.reduce(
    (acc, check) => acc + (getCanActionProfileCheck(check, isClearingUser) ? 1 : 0),
    0,
  );
  const listSections = useMemo(
    () =>
      getRegistrationProfileVersionListItemSummary(
        versionData?.profileVersion,
        data?.profile?.data?.currentVersion?.profileVersionId,
        data?.profile?.data?.pendingVersion?.profileVersionId,
      ),
    [versionData?.profileVersion, data?.profile?.data?.currentVersion?.profileVersionId, data?.profile?.data?.pendingVersion?.profileVersionId],
  );
  const showPending = Boolean(isPending || versionIsPending || error || versionError);
  const isPendingVersion = Boolean(selectedVersionId && data?.profile?.data?.pendingVersion?.profileVersionId === selectedVersionId);

  // Only the current version can be replaced, but we can seed the form with the selected version
  const editFormVersionIdSeed = selectedVersionId === data?.profile?.data?.currentVersion?.profileVersionId ? undefined : selectedVersionId;

  const w9SignedDate = data?.profile ? getW9SignedDateForProfile(data.profile) : undefined;
  const hasW9SignedWithinLastYear = Boolean(data?.profile && checkIfProfileCanBePrimaryAccountHolder(data.profile));

  const headerElement = (
    <PageHeader style={pxOffsetForHeader}>
      <HorizontalStack gap={2} wrap="nowrap">
        {activeTenantContext !== TenantContext.Program ? (
          <ProgramLogomark programId={data?.profile?.programId} shouldLoadLogoMark size="large" withTooltip />
        ) : null}
        <ProfileStatusPill size="large" status={data?.profile?.status} />

        <Pill isPending={showPending} size="large" variant={hasW9SignedWithinLastYear ? 'green' : 'danger'}>
          {w9SignedDate ? `W-9 signed ${w9SignedDate}` : 'W-9 not signed'}
        </Pill>
      </HorizontalStack>

      <RegistrationProfileEntityMenu profileId={profileId} selectedVersionId={selectedVersionId} />
    </PageHeader>
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <WrappingLayoutContainer
          gap={gap}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: (isWrapping) => (
              <VerticalStack gap={gap}>
                {!isWrapping ? headerElement : null}

                <ProfileAccountTiles profileId={profileId} />

                <HorizontalStack align="space-between" blockAlign="center" gap={2}>
                  <Typography.Level1>
                    {match(data?.profile?.data?.currentVersion?.profile || data?.profile?.data?.pendingVersion?.profile)
                      .with(
                        {
                          organization: P.not(P.nullish),
                        },
                        () => 'Organization Info',
                      )
                      .with(
                        {
                          individual: P.not(P.nullish),
                        },
                        () => 'Individual Info',
                      )
                      .otherwise(() => (
                        <Skeleton charCount={12} />
                      ))}
                  </Typography.Level1>

                  <RequiresAccess write>
                    <ButtonLink
                      LinkElement={Link}
                      size="small"
                      to={`/registration/profile/${profileId}/edit${editFormVersionIdSeed ? `?versionId=${editFormVersionIdSeed}` : ''}`}
                    >
                      Edit
                    </ButtonLink>
                  </RequiresAccess>
                </HorizontalStack>

                {isPendingVersion ? (
                  <Hint blockAlign="center" size="small" variant="important" withBorderRadius>
                    This profile version is pending.{' '}
                    {actionableChecks && selectedVersionId && isWriteAllowed ? (
                      <span>
                        <ProfileCheckWorkflowDrawer
                          profileVersionId={selectedVersionId}
                          trigger={
                            <TextButton size="small" typography={statusBannerCtaTypography} underline="always">
                              Review Checks
                            </TextButton>
                          }
                        />{' '}
                        to approve.
                      </span>
                    ) : (
                      'Manual review is needed.'
                    )}
                    {data?.profile?.data?.currentVersion ? (
                      <>
                        {' '}
                        <TextLink
                          LinkElement={Link}
                          size="small"
                          to={`/registration/profile/${profileId}?versionId=${data.profile.data.currentVersion.profileVersionId}`}
                          typography={statusBannerCtaTypography}
                          underline="always"
                        >
                          View active version
                        </TextLink>
                      </>
                    ) : null}
                  </Hint>
                ) : data?.profile?.data?.pendingVersion ? (
                  <Hint blockAlign="center" size="small" variant="important" withBorderRadius>
                    This profile has a pending version.{' '}
                    <TextLink
                      LinkElement={Link}
                      size="small"
                      to={`/registration/profile/${profileId}?versionId=${data.profile.data.pendingVersion.profileVersionId}`}
                      typography={statusBannerCtaTypography}
                      underline="always"
                    >
                      View pending version
                    </TextLink>
                  </Hint>
                ) : null}

                <VerticalStack gap={4}>
                  <AppReadonlyListStack groups={listSections} isPending={showPending} />

                  {versionData?.profileVersion?.data?.profile && 'organization' in versionData.profileVersion.data.profile ? (
                    <VerticalStack gap={4}>
                      <Typography.Level3>Control Persons</Typography.Level3>

                      <OrganizationProfileControlPersonsTable
                        controlPersons={versionData.profileVersion.data.profile.organization.controlPersons || []}
                        primaryContactProfileId={versionData.profileVersion.data.profile.organization.primaryContactProfileId}
                      />
                    </VerticalStack>
                  ) : null}
                </VerticalStack>
              </VerticalStack>
            ),
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...right,
            render: (isWrapping) => (
              <VerticalStack gap={7}>
                {isWrapping ? headerElement : null}
                <WatchlistAndChecksSummary isCompact={isWrapping} profileVersionId={selectedVersionId} />
              </VerticalStack>
            ),
          }}
          wrap="reverse"
          wrapGap={7}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
